import { plus, times } from 'number-precision'
import { ARRIVAL_METHOD } from '@/constants/arrivalMethod'

import { isCatService } from '@/bizComponents/RecipientAddressForm/utils'
import { IParcelFormValues } from '@/store/parcelSlice/types'
import { SHIPMENT_PICKUP_TYPE } from '@/store/serviceProviderSlice/enum'
import { COD_TYPE_ENUM } from '@/store/serviceProviderSlice/types'
import { isUndefined } from 'lodash'
import dayjs from 'dayjs'
import { isCodCurrencyCodeSpecified } from '@/utils'

import * as TYPES from './types'
export * from './types'

/* format发货地址 */
export const formatSenderAddressToService = (params: TYPES.IFormaSenderAddress) => {
  return {
    senderReferenceNo: params?.senderReferenceNo,
    senderCountryCode: params.departure_countryCode,
    senderProvince: params?.departure_area?.province,
    senderCity: params?.departure_area?.city,
    senderDistrict: params?.departure_area?.district,
    senderTown: params?.departure_area?.town,
    senderName: params.departure_fullName,
    senderFirstName: params.departure_firstName,
    senderLastName: params.departure_lastName,
    senderMobile: params?.departure_mobile,
    senderAddress: params?.departure_address,
    senderAddressTwo: params?.departure_addressTwo,
    senderZipCode: params?.departure_zipCode,
    senderEmail: params?.departure_email,
    senderRemark: params.parcel_sender_remark,
  }
}

/* 收货地址 */
export const formatRecipientAddressToService = (params: TYPES.IFormatRecipientAddress) => {
  const { arrival_method } = params
  // 相同的信息
  const sameInfo = {
    consigneeName: params?.arrival_fullName,
    consigneeFirstName: params.arrival_firstName,
    consigneeLastName: params.arrival_lastName,
    consigneeMobile: params?.arrival_mobile,
    consigneeEmail: params?.arrival_email,
    consigneeIdNo: params?.provider_consignee_id_num,
    consigneeIdType: params?.provider_consignee_id_type,
  }
  // 宅配
  if (arrival_method === ARRIVAL_METHOD.home) {
    return {
      consigneeCountryCode: params.arrival_countryCode,
      consigneeProvince: params?.arrival_area?.province,
      consigneeCity: params?.arrival_area?.city,
      consigneeDistrict: params?.arrival_area?.district,
      consigneeTown: params?.arrival_area?.town,
      consigneeAddress: params?.arrival_address,
      consigneeAddressTwo: params?.arrival_addressTwo,
      consigneeZipCode: params?.arrival_zipCode,
      ...sameInfo,
    }
  }

  const store711AddressLength = params.arrival_store_711station?.address?.length
  // 地址1最大长度
  const maxAddressLength = 256

  /** 黑猫特殊处理 */
  const station711Data = isCatService(params.arrival_store_companyCode)
    ? {
        consigneeAddress: params.arrival_store_711station?.address?.slice(0, maxAddressLength),
        consigneeAddressTwo: params.arrival_store_711station?.address?.slice(maxAddressLength, store711AddressLength),
        consigneeStationName: params.arrival_store_711station?.name,
        consigneeStationCode: params.arrival_store_711station?.storeId,
        consigneeProvince: params.arrival_store_711station?.province,
        consigneeCity: params.arrival_store_711station?.city,
        consigneeDistrict: params.arrival_store_711station?.district,
      }
    : {}

  return {
    consigneeCountryCode: params.arrival_countryCode,
    consigneeProvince: params?.arrival_store_area?.province,
    consigneeCity: params?.arrival_store_area?.city,
    consigneeDistrict: params?.arrival_store_area?.district,
    consigneeStationSeq: params.arrival_store_station,
    consigneeStationType: params?.arrival_station_type ? String(params?.arrival_station_type) : undefined,
    consigneeZipCode: undefined,
    ...sameInfo,
    ...station711Data,
  }
}

/** 转换包裹信息 */
export const formatParcelToService = (params: IParcelFormValues) => {
  const { parcel_check_currency, parcel_converted_currency_code, parcel_commodities, parcel_currency_code, parcel_show_keys } = params
  return (
    parcel_commodities?.map((item) => {
      return {
        goodsName: item.parcel_goods_name,
        goodsCode: item.parcel_goods_code,
        goodsQuantity: item.parcel_goods_quantity ? Number(item.parcel_goods_quantity) : undefined,
        goodsPrice: times(Number(parcel_check_currency ? item.parcel_goods_converted_price : item.parcel_goods_price), 100),
        currencyCode: parcel_check_currency ? parcel_converted_currency_code : parcel_currency_code,
        goodsCname: item.parcel_goods_cname || '',
        goodsEname: item.parcel_goods_ename || '',
        goodsHscode: item.parcel_goods_hscode || '',
        goodsSize: item.parcel_goods_size || '',
        originCountryCode: parcel_show_keys?.includes('origin_country_code') ? item.origin_country_code : undefined,
        // 转换后加上原单价parcel_goods_price
        ...(parcel_check_currency && { originGoodsPrice: times(Number(item.parcel_goods_price), 100) }),
        goodsMaterial: item.parcel_goods_material || '',
        goodsPurpose: item.parcel_goods_purpose || '',
        goodsWeight: parcel_show_keys?.includes('parcel_goods_weight') && item.parcel_goods_weight ? Number(item.parcel_goods_weight) : undefined,
      }
    }) || []
  )
}

/** 转换费用信息 */
export const formatFeeToService = (params: TYPES.IFormatFee) => {
  const { parcel_check_currency, parcel_converted_currency_code, parcel_currency_code, provider_payment_type, provider_custom_freight_currency_code, feeItems } = params
  const codAmountData = getCodAmountDatas(params)

  return {
    freight: params.freight || params.estimatedCost,
    payType: params.provider_pay_type,
    ...codAmountData,
    sellerCustomFreightCurrencyCode: provider_custom_freight_currency_code,
    discountFreight: isUndefined(params.discountFreight) ? params?.feeInfo?.discountFreight : params.discountFreight,
    originalFreight: isUndefined(params.originalFreight) ? params?.feeInfo?.originalFreight : params.originalFreight,
    currencyCode: parcel_check_currency ? parcel_converted_currency_code : parcel_currency_code,
    paymentType: provider_payment_type,
    monthlyAccount: params.provider_monthly_account,
    freightCurrencyCode: params.freightCurrencyCode,
    deductedFreight: codAmountData?.sellerCustomFreight && params?.deductedFreight,
    deductedFreightCurrencyCode: codAmountData?.sellerCustomFreight && params?.deductedFreightCurrencyCode,
    // 勾选了自动转换：
    ...(parcel_check_currency && {
      currencyConverted: 1,
      currencyRate: params.parcel_currency_rate,
      fetchTime: params.parcel_rate_fetch_time,
      originCurrencyCode: parcel_currency_code,
      codOverwrite: parcel_check_currency && provider_payment_type !== COD_TYPE_ENUM.notCod, // cod和字段转换是否冲突
    }),
    feeItems,
  }
}

/** 转换扩展信息 */
export const formatExtraInfo = (params: TYPES.IFormatExtraInfo) => {
  const [provider_delivery_date, provider_delivery_timeRang] = params.provider_delivery_dates || []

  return {
    storageCdt: params?.arrival_store_storage_cdt,
    pickupType: params.provider_pickup_type,
    customIdNo: params.provider_custom_id_no || '',
    deliveryType: params.arrival_method === 1 ? 1 : 4,
    taxes: params.provider_tax_type
      ? [
          {
            taxType: params.provider_tax_type,
            taxNo: params.provider_tax_no,
          },
        ]
      : undefined,
    requiredDeliveryDates: provider_delivery_date ? [provider_delivery_date] : [],
    requiredDeliveryTimeslots: provider_delivery_timeRang ? [provider_delivery_timeRang] : [],
    packageDesc: params?.provider_package_desc?.trim(),
    messageTraceNotifyStatus: params.provider_message_trace_notify_status,
  }
}

/** 转换其他乱七八糟的东西 */
export const formatOthers = (params: TYPES.IFormatOthers) => {
  const { provider_pickup_dates } = params
  const [provider_pickup_date, provider_pickup_time_slots] = provider_pickup_dates || []
  return {
    expressTypeCode: params.expressTypeCode,
    weight: params?.parcel_weight || undefined,
    weightUnit: params.parcel_weight_unit || 'kg',
    length: params.parcel_length || undefined,
    width: params.parcel_width || undefined,
    height: params.parcel_height || undefined,
    lengthUnit: params.parcel_length_unit || 'cm',
    requiredPickupDates: params.provider_pickup_type === SHIPMENT_PICKUP_TYPE.pickUp && provider_pickup_date ? [provider_pickup_date] : [],
    requiredPickupTimeslots: params.provider_pickup_type === SHIPMENT_PICKUP_TYPE.pickUp && provider_pickup_time_slots ? [provider_pickup_time_slots] : [],
    timezone: params.timeZone,
    channelSellerId: params.provider_channel_seller_id,
    bindOneshipSalesOrder: params.bindOneshipSalesOrder,
    orderNo: params?.parcel_order_no,
    asyncSend: params.asyncSend,
  }
}

/** 转换渠道特性字段 */
export const formatChannelCustomInfo = (params: TYPES.IShipFormValues & { companyCode?: string }) => {
  const { provider_taxation, provider_taxationCurrencyCode, provider_pickup_station_code } = params
  const companyCode = params.companyCode.toLowerCase()

  const pickUpStationCodeObj = provider_pickup_station_code
    ? {
        pickUpStationCode: provider_pickup_station_code,
      }
    : undefined
  const channelCustomInfo = {
    jne:
      provider_taxation || provider_taxationCurrencyCode
        ? {
            taxation: params.provider_taxation ? times(Number(params.provider_taxation), 100) : undefined,
            taxationCurrencyCode: provider_taxationCurrencyCode,
          }
        : undefined,
    jdwl: pickUpStationCodeObj,
    hksf: pickUpStationCodeObj,
  }
  return {
    invoiceNo: params?.provider_invoice_no?.trim(),
    invoiceDate: params?.provider_invoice_date ? dayjs(params?.provider_invoice_date).format('YYYY-MM-DD') : undefined,
    invoicePath: params?.provider_invoice_path,
    serviceTypes: params?.provider_service_types,
    hazmatTypeCode: params?.provider_hazmat_type_code,
    ...(channelCustomInfo[companyCode] ? { [companyCode]: channelCustomInfo[companyCode] } : {}),
    consigneeFirstName: params?.arrival_firstName,
    consigneeLastName: params?.arrival_lastName,
    senderFirstName: params?.departure_firstName,
    senderLastName: params?.departure_lastName,
  }
}

/** oneship客户端转服务端数据 */
export const clientToService = (params: TYPES.IClientToService) => {
  const { fieldsValue, ext } = params

  const address = {
    ...formatSenderAddressToService({ ...fieldsValue, ...ext }),
    ...formatRecipientAddressToService({ ...fieldsValue, ...ext }),
  }

  const commodities = formatParcelToService({ ...fieldsValue })

  const feeInfo = formatFeeToService({ ...fieldsValue, ...ext })

  const extraInfo = formatExtraInfo({ ...fieldsValue })

  const others = formatOthers({ ...fieldsValue, ...ext })

  const channelCustomInfo = formatChannelCustomInfo({ ...fieldsValue, ...ext })

  return {
    address,
    commodities,
    feeInfo,
    extraInfo,
    channelCustomInfo,
    ...others,
  }
}

type AmountParams = Partial<
  Pick<
    TYPES.IFormatFee,
    | 'codCurrencyCode'
    | 'provider_payment_type'
    | 'provider_custom_freight'
    | 'provider_custom_freight_currency_code'
    | 'targetCodCurrencyCode'
    | 'codCurrencyType'
    | 'provider_cod_amount'
  >
>

export const getCodAmountDatas = (params: AmountParams) => {
  const { codCurrencyCode, provider_payment_type, provider_custom_freight, provider_custom_freight_currency_code, targetCodCurrencyCode, codCurrencyType, provider_cod_amount } =
    params

  const isCodOption = provider_payment_type === COD_TYPE_ENUM.cod
  const sellerCodAmount = isCodOption ? (provider_cod_amount ? times(Number(params.provider_cod_amount), 100) : undefined) : undefined

  /* 指定COD币种的服务产品，cod币种为指定币种 */
  const finalCodCurrencyCode = isCodCurrencyCodeSpecified(codCurrencyType) ? targetCodCurrencyCode : codCurrencyCode

  let codAmount = sellerCodAmount
  let sellerCustomFreight = undefined

  if (isCodOption && provider_custom_freight && provider_custom_freight_currency_code === finalCodCurrencyCode) {
    sellerCustomFreight = times(Number(provider_custom_freight), 100)
    codAmount = plus(sellerCustomFreight || 0, codAmount || 0)
  }

  return {
    codAmount,
    sellerCodAmount,
    sellerCustomFreight,
    codCurrencyCode: finalCodCurrencyCode,
  }
}
